/**
 * Off-Canvas Menu Toggle Functionality
 *
 * Mobile-only off-canvas menu with accordion support.
 * Desktop uses horizontal navigation with mega menu (see desktop-mega-menu.js).
 *
 * Note: Accordion functionality is provided by THS App (ths-app/src/assets/js/components.js)
 * which must be enqueued before this script.
 *
 * @since 2.0.0
 */

import { TIMING, ScrollIndicator, closeAccordions } from './menu-utils.js'
import { NAV_MOBILE_MQ } from './breakpoints.js'

/**
 * Initialize off-canvas menu functionality (mobile only)
 */
export function initOffCanvasMenu () {
  // Check if we're on mobile
  const isMobile = () => NAV_MOBILE_MQ.matches

  const menuToggle = document.querySelector('.site-header__menu-toggle')
  const primaryNav = document.querySelector('.site-header__navigation')
  const body = document.body

  // Create overlay element if it doesn't exist
  let overlay = document.querySelector('.menu-overlay')
  if (!overlay) {
    overlay = document.createElement('div')
    overlay.className = 'menu-overlay'
    overlay.setAttribute('aria-hidden', 'true')
    body.appendChild(overlay)
  }

  if (!menuToggle || !primaryNav) {
    return
  }

  // Scroll indicator instance
  let scrollIndicator = null

  // Timer used to remove the is-resizing class after resize events stop
  let resizeTimer = null

  /**
   * Initialize scroll indicator
   */
  const initScrollIndicator = () => {
    if (scrollIndicator) {
      scrollIndicator.detach()
    }

    scrollIndicator = new ScrollIndicator(primaryNav, 'show-bottom-indicator')
    scrollIndicator.attach()

    // Listen for accordion state changes
    const accordionTriggers = primaryNav.querySelectorAll('[data-accordion-trigger]')
    accordionTriggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        // Wait for accordion animation to complete
        setTimeout(() => scrollIndicator.update(), TIMING.INDICATOR_DELAY)
      })
    })

    // Initial check after menu opens and settles
    setTimeout(() => scrollIndicator.update(), TIMING.INDICATOR_DELAY)
  }

  /**
   * Toggle menu open/closed (mobile only)
   */
  const toggleMenu = () => {
    // Only allow toggle on mobile
    if (!isMobile()) {
      return
    }

    const isOpen = body.classList.contains('off-canvas-menu-open')

    if (isOpen) {
      closeMenu()
    } else {
      openMenu()
    }
  }

  /**
   * Open the off-canvas menu
   */
  const openMenu = () => {
    // Reset scroll position to top
    if (primaryNav) {
      primaryNav.scrollTop = 0
    }

    body.classList.add('off-canvas-menu-open')

    // Update ARIA attributes
    menuToggle.setAttribute('aria-expanded', 'true')
    overlay.setAttribute('aria-hidden', 'false')

    // Add gradient class after slide animation completes
    setTimeout(() => {
      body.classList.add('show-gradient')
    }, TIMING.MENU_SLIDE)

    // Initialize scroll indicator
    initScrollIndicator()
  }

  /**
   * Close the off-canvas menu
   */
  const closeMenu = () => {
    body.classList.remove('off-canvas-menu-open')
    body.classList.remove('show-gradient')

    // Update ARIA attributes
    menuToggle.setAttribute('aria-expanded', 'false')
    overlay.setAttribute('aria-hidden', 'true')

    // Return focus to the toggle so keyboard users don't lose their place
    // in the DOM after closing the menu (WCAG 2.4.3 Focus Order, AA).
    menuToggle.focus()

    // Detach scroll indicator
    if (scrollIndicator) {
      scrollIndicator.detach()
      scrollIndicator = null
    }

    // Close all accordions in the navigation menu
    closeAccordions(primaryNav)
  }

  /**
   * Handle Escape key to close menu (mobile only)
   */
  const handleEscapeKey = (e) => {
    if (e.key === 'Escape' && body.classList.contains('off-canvas-menu-open') && isMobile()) {
      closeMenu()
    }
  }

  /**
   * Handle viewport changes - close menu if resized to desktop.
   *
   * Also suppresses the off-canvas slide transition during resize so that
   * crossing the mobile breakpoint does not cause a FOUC where the nav
   * briefly appears on-screen before animating off to the right.
   * The is-resizing class on <body> disables the transition in CSS for the
   * duration of the resize interaction.
   */
  const handleResize = () => {
    // Disable nav transition for the duration of the resize
    body.classList.add('is-resizing')
    clearTimeout(resizeTimer)
    resizeTimer = setTimeout(() => {
      body.classList.remove('is-resizing')
    }, TIMING.MENU_SLIDE)

    if (!isMobile() && body.classList.contains('off-canvas-menu-open')) {
      closeMenu()
    }
  }

  // Event listeners
  menuToggle.addEventListener('click', toggleMenu)
  overlay.addEventListener('click', closeMenu)
  document.addEventListener('keydown', handleEscapeKey)
  window.addEventListener('resize', handleResize)

  // Header menu accordion is auto-initialized by THS App
  // (see ths-app/src/assets/js/components.js)
  // Default behavior: only one panel open at a time, which is what we want
  // Note: Accordion only functions on mobile (desktop uses mega menu)

  // Return cleanup function to remove event listeners
  return {
    destroy () {
      menuToggle.removeEventListener('click', toggleMenu)
      overlay.removeEventListener('click', closeMenu)
      document.removeEventListener('keydown', handleEscapeKey)
      window.removeEventListener('resize', handleResize)
      clearTimeout(resizeTimer)
      body.classList.remove('is-resizing')
      if (scrollIndicator) {
        scrollIndicator.detach()
      }
    },
    closeMenu
  }
}

/**
 * Close the off-canvas menu (public API)
 * Can be called from other modules (e.g., modal)
 */
export function closeOffCanvasMenu () {
  const body = document.body
  if (!body.classList.contains('off-canvas-menu-open')) {
    return
  }

  const menuToggle = document.querySelector('.site-header__menu-toggle')
  const overlay = document.querySelector('.menu-overlay')
  const primaryNav = document.querySelector('.site-header__navigation')

  body.classList.remove('off-canvas-menu-open')
  body.classList.remove('show-gradient')

  if (menuToggle) {
    menuToggle.setAttribute('aria-expanded', 'false')
  }

  if (overlay) {
    overlay.setAttribute('aria-hidden', 'true')
  }

  // Close all accordions in the navigation menu
  closeAccordions(primaryNav)
}
